import type { FocusEventHandler } from 'react';
import type {
	FieldPath,
	FieldPathValue,
	FieldValues,
	UseControllerProps,
} from 'react-hook-form';
import type { AutoCompleteItem } from './AutoCompleteItem.d';

import { isShowIfFunction, type ShowIfFunction } from './isShowIfFunction';

import { isStringLiteral } from '@enonic/js-utils/value/isStringLiteral';
import {
	type AutocompleteProps,
	Autocomplete as FriskAutocomplete,
} from '@hdir/ui/autocomplete';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	useController,
	useWatch,
} from 'react-hook-form';
import { useFormContext } from './FormContext';

export type { AutocompleteProps } from '@hdir/ui/autocomplete';

export function AutoComplete<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	disabled: disabledProp,
	label,
	errorSummaryLabel = isStringLiteral(label) ? label : undefined,
	items = [],
	multiselect = false,
	name,
	onBlur,
	rules,
	showIf,
	...otherProps
}: Omit<AutocompleteProps<AutoCompleteItem>,
	| 'defaultValue'
	| 'error'
	| 'getDisplayName'
	| 'getItemId'
	| 'name'
	| 'onBlur'
	| 'onChange'
	| 'multiselect' // TODO Frisk has false, rather than boolean.
	| 'ref'
	| 'value'
> & {
	errorSummaryLabel?: string;
	name: TName;
	multiselect?: boolean; // TODO Frisk has false, rather than boolean.
	onBlur?: FocusEventHandler<HTMLInputElement|HTMLLabelElement> & FocusEventHandler<HTMLLabelElement>;
	rules?: UseControllerProps<TFieldValues, TName>['rules'];
	showIf?: ShowIfFunction<TFieldValues>;
}): React.JSX.Element | null {
	const {
		control,
		getRequired,
		setErrorSummaryLabel,
		setFocusRef,
		// setScrollRef,
	} = useFormContext<TFieldValues>();
	const required: boolean = rules
		? !!rules.required
		: getRequired
			? getRequired(name)
			: false;
	const {
		field: {
			disabled: useControllerDisabled,
			onBlur: useControllerOnBlur,
			onChange: useControllerOnChange,
			// ref: useControllerFieldRef,
			value,
		},
		fieldState: {
			error,
		},
	} = useController<TFieldValues, TName>({
		control,
		disabled: disabledProp,
		name,
		rules,
	});
	const [hydrated, setHydrated] = useState(false);
	const disabled = !hydrated || useControllerDisabled;

	const focusRef = useRef<HTMLInputElement>();
	// Since we're typically using hideLabel and building the label outside this compoent,
	// we'll also have to set the scrollref where the label is created...
	// const scrollRef = useRef<HTMLDivElement>();

	const idToLabel: Record<AutoCompleteItem['id'], AutoCompleteItem['label']> = useMemo(() => {
		const obj: Record<AutoCompleteItem['id'], AutoCompleteItem['label']> = {};
		for (const item of items) {
			const { id, label } = item;
			obj[id] = label;
		}
		return obj;
	}, [items]);

	useEffect(() => { // NOTE: useEffect is part of logic and MUST come before any return statement!
		if (errorSummaryLabel) {
			setErrorSummaryLabel({field: name, label: errorSummaryLabel});
		}
	}, [errorSummaryLabel]);

	useEffect(() => { // NOTE: useEffect is part of logic and MUST come before any return statement!
		if (focusRef.current) {
			setFocusRef({ field: name, el: focusRef.current });
		}
	}, [focusRef.current]);

	// useEffect(() => { // NOTE: useEffect is part of logic and MUST come before any return statement!
	// 	if (scrollRef.current) {
	// 		setScrollRef({ field: name, el: scrollRef.current });
	// 	}
	// }, [scrollRef.current]);

	useEffect(() => { // NOTE: useEffect is part of logic and MUST come before any return statement!
		setHydrated(true);
	}, []);

	if (isShowIfFunction(showIf) && !showIf({
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		watch: (watchParams) => useWatch({...watchParams, control}) as FieldPathValue<TFieldValues, FieldPath<TFieldValues>>,
	})) {
		return null;
	}

	return (
		// <div
		// 	ref={(divElOrNull) => {
		// 		if (divElOrNull) scrollRef.current = divElOrNull;
		// 	}}
		// >
		// @ts-expect-error Too difficult to type this, at least right now.
		<FriskAutocomplete<AutoCompleteItem>
			{...otherProps}
			disabled={disabled}
			error={error ? error.message : null}
			getDisplayName={({label}) => label}
			getItemId={({id}) => id}
			items={items}
			label={label}
			multiselect={
				// TODO Frisk has false, rather than boolean.
				multiselect as false | undefined
			}
			name={name}

			onBlur={(e) => {
				// https://react-hook-form.com/docs/usecontroller
				// A function which sends the input's onBlur event to the library.
				// It should be assigned to the input's onBlur prop.
				useControllerOnBlur();

				if (onBlur) onBlur(e);
			}}

			onChange={(item: AutoCompleteItem|AutoCompleteItem[]) => {
				if (Array.isArray(item)) {
					useControllerOnChange(item.map(({id}) => id));
				} else {
					const {id} = item || {};
					useControllerOnChange(id);
				}

			}}

			onRemoved={(a) => {
				console.log('AutoComplete onRemoved', a); // TODO
			}}

			ref={(inputElOrNull) => {
				if (inputElOrNull) {
					focusRef.current = inputElOrNull;
				}
			}}

			required={required}

			value={value
				? Array.isArray(value)
					? value.map((id: AutoCompleteItem['id']) => ({ // eslint-disable-line @typescript-eslint/no-unsafe-call
						id,
						label: idToLabel[id],
					})) as AutoCompleteItem[]
					: {
						id: value as string,
						label: idToLabel[value as string],
					} as AutoCompleteItem
				: null
			}

		/>
		// </div>
	);
}
